import axios from '@/api/axios';
import { gql } from '@/api/utils';

const orderFields = gql`
id
status
city {
  baseUrl
  title
  logo {
    original
  }
  noLicensing
  organization
  letterLogo {
    original
    imageAltText
  }
  taxRate
  taxCode
  taxNumber
  name
  safeAndHappyFund
  useCustomDonationAmounts
  roundUpMyOrder
  donationAmounts{
    amount
  }
  partner {
    name
    product {
      id
    }
    image {
      original
    }
  }
  paymentProcessor {
    processor
    publicKey
  }
  paymentScreen
  locale
  province
  country
  skipDonation
  donationSectionTitle
  donationSectionDescription
  donationSectionImage {
    original
  }
  customerService {
    phoneNumber
  }
  petOwnerOrderMessage
  showWalletCardProducts
}
petOwner {
  id
  primaryPhone
  pets {
    id
    name
    images {
      id
      original
    }
  }
  renewalPets: petsUpForRenewal {
    id
    name
    type
    activeLicence {
      expiry
      expiryDateTime
      name
      startDate
      renewalDate
      tempLicense
    }
  }
}
subtotal
shipping {
  type
  name
  description
  fee
}
hasPriorityTrackedShipping
eligibleForPriorityTrackedShipping
canDowngradeToRegularShipping
shippingFeeOptions {
  type
  name
  description
  fee
}
tax
processingFee
discountAmount
discountAmountContext {
  discountAmount
  confirmationContext {
    success
  }
}
roundUpAmount
discount {
  code
  includeSaleItems
}
total
pets {
  id
}
groups {
  id
  pet {
    id
  }
  walletCardConfigurations {
    optedIn
    completed
    url
    orderItemBundle {
      badge {
        text
        color
        backgroundColor
      }
    }
  }
  freePetTagContext {
    groupNotificationText
    containsFreePetTagWithReplacementFee  
  }
}
itemsCount
items {
  id
  discount
  subtotal
  total
  customization
  quantity
  isCompleted
  customTagOrderInfo {
    name
    phone
  }
  addons {
    type
    title
    included
    retailPrice
    discountPrice
    savingAmount
  }
  parent {
    id
    product {
      id
      ... on DesignerTagProduct {
        style {
          id
          code
        }
      }
    }
  }
  product {
    id
    name
    type
    price
    type
    description
    image {
      original
    }
    ... on DesignerTagProduct {
      size
      color
      style {
        id
        code
      }
      municipalTag
      personalization {
        price
      }
      material {
        material
      }
      laserPrinterJig {
        shape
        material
      }
      keyChainPrice
      keyChainDiscountedPrice
      discountedPrice
    }
    ... on PetTagProduct {
      size
      municipalTag
      style {
        id
        code
      }
      discountedPrice
    }
    ... on CollarProduct {
      size
      color
      backImage {
        original
      }
      personalization {
        price
      }
      style {
        id
        code
        productGroups {
          id
        }
      }
      discountedPrice
    }
    ... on LeashProduct {
      color
      length
      backImage {
        original
      }
      style {
        id
        code
        productGroups {
          id
        }
      }
      discountedPrice
    }
    ... on PetEssentialsProduct {
      status
      color
      size
      image {
        original
      }
      style {
        id
        code
        productGroups {
          id
        }
      }
    }  
    ... on AdditionalFeeProduct {
      subType
    }
    ... on LateFeeProduct {
      subType
    }
    ... on VoiceAndSightProduct {
      subType
    }
  }
  group {
    id
    pet {
      id
      name
      idCode
      licenses {
        id
       }
    }
  }
  children {
    id
    discount
    subtotal
    total
    orderItemBundle {
      id
    }
    group {
      pet {
        name
      }
    }
    parent {
      id
      product {
        id
        ... on DesignerTagProduct {
          style {
            id
            code
          }
        }
      }
    }
    product {
      id
      name
      type
      price
      description
      image {
        original
      }
      ... on DesignerTagProduct {
        keyChainPrice
        keyChainDiscountedPrice
      }
      ... on PetEssentialsProduct {
        sku
      }
    }
    keyChain
  }
  discount
  orderItemBundle {
    id
    badge {
      text
      color
      backgroundColor
    }
  }
}
ableToCheckout
prepareOrderForPaymentContext {
  removedItems
}
`;

export default {
  async getOrder(id) {
    try {
      const query = gql`
        query order($id: ID!) {
          order(id: $id) {
           ${orderFields}
          }
        }
      `;

      const variables = { id };
      const { order: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async applyDiscount(id, code) {
    try {
      const query = gql`
        mutation applyDiscount($id: ID! $code: String!){
          applyDiscount(order: $id, code: $code){
            ${orderFields}
          } 
        }
      `;

      const variables = { id, code };
      const { applyDiscount: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async removeDiscount(id) {
    try {
      const query = gql`
        mutation removeDiscount($id: ID!){
          removeDiscount(orderId: $id){
            ${orderFields}
          } 
        }
      `;

      const variables = { id };
      const { removeDiscount: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async orderDonation(id, amount) {
    try {
      const query = gql`
        mutation orderDonation($id: ID! $amount: Float!){
          orderDonation(orderId: $id, amount: $amount){
            ${orderFields}
          } 
        }
      `;

      const variables = { id, amount: parseFloat(amount) };
      const { orderDonation: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async pay(id, token, amount, setDefaultPaymentMethod = false, autoRenewalConfig = null, marketingCommunicationOptIn = null) {
    try {
      if (token) {
        const query = gql`
        mutation pay($id: ID!, $token: String!, $tokenType: String!, $amount: Float!, $setDefaultPaymentMethod: Boolean, $autoRenewalConfig: AutorenewalConfigInput, $marketingCommunicationOptIn: Boolean){
          pay(orderId: $id, token: {token: $token, type: $tokenType}, amount: $amount, setDefaultPaymentMethod: $setDefaultPaymentMethod, autoRenewalConfig: $autoRenewalConfig, marketingCommunicationOptIn: $marketingCommunicationOptIn){
            ${orderFields}
          } 
        }
      `;
        const variables = {
          id,
          token: token.token,
          tokenType: token.type,
          amount,
          setDefaultPaymentMethod,
          autoRenewalConfig,
          marketingCommunicationOptIn,
        };
        const { pay: results } = await axios.post(`/`, { query, variables });
        return results;
      } else {
        const query = gql`
        mutation pay($id: ID!, $amount: Float!, $setDefaultPaymentMethod: Boolean, $autoRenewalConfig: AutorenewalConfigInput, $marketingCommunicationOptIn: Boolean){
          pay(orderId: $id, amount: $amount, setDefaultPaymentMethod: $setDefaultPaymentMethod, autoRenewalConfig: $autoRenewalConfig, marketingCommunicationOptIn: $marketingCommunicationOptIn){
            ${orderFields}
          } 
        }
      `;
        const variables = { id, amount, setDefaultPaymentMethod, autoRenewalConfig, marketingCommunicationOptIn };
        const { pay: results } = await axios.post(`/`, { query, variables });
        return results;
      }
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async removeItem(orderId, itemId) {
    try {
      const query = gql`
        mutation removeItem($orderId: ID!, $itemId: ID!){
          removeItem(orderId: $orderId, itemId: $itemId){
            ${orderFields}
          } 
        }
      `;

      const variables = { orderId, itemId };
      const { removeItem: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async removePersonalization(orderId, itemId) {
    try {
      const query = gql`
        mutation removePersonalization($orderId: ID!, $itemId: ID!){
          removePersonalization(orderId: $orderId, itemId: $itemId){
            ${orderFields}
          } 
        }
      `;

      const variables = { orderId, itemId };
      const { removePersonalization: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async addItem(orderId, productDetails) {
    try {
      const query = gql`
        mutation AddItemMutation($orderId: ID!, $productDetails: [OrderProductInput!]) {
          addItem(orderId: $orderId, products: $productDetails) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, productDetails: [productDetails] };
      const { addItem: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async addPersonalization(orderId, { personalization, itemId }) {
    try {
      const query = gql`
        mutation addPersonalization($orderId: ID!, $itemId: ID!, $personalization: PersonalizationInput!) {
          addPersonalization(orderId: $orderId, itemId: $itemId, personalization: $personalization) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, itemId, personalization };
      const { addPersonalization: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async temporaryOrder(cityId, products, discountCode) {
    try {
      const query = gql`
        mutation ($cityId: ID!, $products: [OrderProductInput]!, $discountCode: String) {
          temporaryOrder(cityId: $cityId, products: $products, discountCode: $discountCode) {
            status
            city {
              baseUrl
              title
              logo {
                original
              }
              taxRate
              taxCode
              taxNumber
              name
              locale
              province
              country
              customerService {
                phoneNumber
              }
              skipDonation
            }
            subtotal
            shipping {
              type
              name
              description
              fee
            }
            tax
            processingFee
            discountAmount
            discount {
              code
              includeSaleItems
            }
            total
            itemsCount
            hasPriorityTrackedShipping
            eligibleForPriorityTrackedShipping
            canDowngradeToRegularShipping
            items {
              discount
              subtotal
              total
              customization
              customTagOrderInfo {
                name
                phone
              }
              product {
                id
                name
                type
                price
                image {
                  original
                }
                ... on DesignerTagProduct {
                  size
                  color
                  style {
                    id
                    code
                  }
                  municipalTag
                  personalization {
                    price
                  }
                  laserPrinterJig {
                    shape
                    material
                  }
                  keyChainPrice
                  keyChainDiscountedPrice
                  discountedPrice
                }
                ... on PetTagProduct {
                  size
                  municipalTag
                  style {
                    id
                    code
                  }
                  discountedPrice
                }
                ... on CollarProduct {
                  size
                  color
                  backImage {
                    original
                  }
                  personalization {
                    price
                  }
                  style {
                    id
                    code
                    productGroups {
                      id
                    }
                  }
                  discountedPrice
                }
                ... on LeashProduct {
                  color
                  length
                  backImage {
                    original
                  }
                  style {
                    id
                    code
                    productGroups {
                      id
                    }
                  }
                  discountedPrice
                }
                ... on PetEssentialsProduct {
                  status
                  color
                  size
                  image {
                    original
                  }
                  style {
                    id
                    code
                    productGroups {
                      id
                    }
                  }
                }
                ... on AdditionalFeeProduct {
                  subType
                }
                ... on LateFeeProduct {
                  subType
                }
              }
              parent {
                total
                product {
                  id
                  ... on DesignerTagProduct {
                    style {
                      id
                      code
                    }
                  }
                }
              }
              children {
                discount
                subtotal
                total
                parent {
                  total
                  product {
                    id
                    ... on DesignerTagProduct {
                      style {
                        id
                        code
                      }
                    }
                  }
                  customTagOrderInfo {
                    name
                    phone
                  }
                }
                product {
                  id
                  name
                  type
                  price
                  image {
                    original
                  }
                  ... on DesignerTagProduct {
                    keyChainPrice
                    keyChainDiscountedPrice
                  }
                  ... on PetEssentialsProduct {
                    sku
                  }
                }
                keyChain
              }
              discount
              keyChain
            }
          }
        }
      `;
      const variables = { cityId, products, discountCode };
      const { temporaryOrder: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async cancelOrder(orderId) {
    try {
      const query = gql`
        mutation CancelOrderMutation($orderId: ID!) {
          cancelOrder(orderId: $orderId){
            ${orderFields}
          } 
        }
      `;

      const variables = { orderId };
      const { cancelOrder: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async upgradeToPriorityShipping(orderId) {
    try {
      const query = gql`
        mutation UpgradeToPriorityTrackedShippingMutation($orderId: ID!) {
          addPriorityTrackedShipping(orderId: $orderId){
            ${orderFields}
          } 
        }
      `;

      const { addPriorityTrackedShipping: results } = await axios.post(`/`, { query, variables: { orderId } });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async downgradeToRegularShipping(orderId) {
    try {
      const query = gql`
        mutation DowngradeToRegularMailShippingMutation($orderId: ID!) {
          removePriorityTrackedShipping(orderId: $orderId){
            ${orderFields}
          } 
        }
      `;

      const { removePriorityTrackedShipping: results } = await axios.post(`/`, { query, variables: { orderId } });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async removeItemsByProduct(orderId, productId, quantity) {
    try {
      const query = gql`
        mutation removeItemsByProduct($orderId: ID!, $productId: ID!, $quantity: Float!) {
          removeItemsByProduct(orderId: $orderId, productId: $productId, quantity: $quantity) {
            ${orderFields}
          }
        }
      `;

      const variables = { orderId, productId, quantity: Number(quantity) };
      const { removeItemsByProduct: results } = await axios.post(`/`, { query, variables });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async removeWalletCard(input) {
    try {
      const query = gql`
        mutation updateOrderForPet($input: UpdateOrderForPetInput!) {
          updateOrderForPet(input: $input) {
            ${orderFields}
          }
        }
      `;
      const variables = {
        input,
      };
      const { updateOrderForPet: results } = await axios.post(`/`, { query, variables });

      return Promise.resolve(results);
    } catch (error) {
      return Promise.reject({
        message: error.message,
      });
    }
  },
  async prepareOrderForPayment(input) {
    try {
      const query = gql`
        mutation prepareOrderForPayment($input: PrepareOrderForPaymentInput!) {
          prepareOrderForPayment(input: $input) {
            ${orderFields}
          }
        }
      `;
      const variables = {
        input,
      };
      const { prepareOrderForPayment: results } = await axios.post(`/`, { query, variables });

      return results;
    } catch (error) {
      throw new Error(error?.message);
    }
  },
};
